/**
 * Agent function execution history — `GET /agent-functions/{functionId}/requests`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader` + `CordioAbpPagedDataTable`, with the grid owning paging and filtering. The page's
 * `filters` / `totalCount` state and the `loadData` callback that refetched on every change are
 * gone; `listVersion` is the single piece of refetch state left.
 *
 * ─── Column plan ────────────────────────────────────────────────────────────────────────────────
 * field      │ header   │ width │ cell type                │ filter
 * ───────────┼──────────┼───────┼──────────────────────────┼──────────────────────────────────────
 * status     │ Status   │  14%  │ custom Chip              │ status tabs → synthetic `statusTab`
 * inputJson  │ Input    │  26%  │ text (read-more 80)      │ —
 * outputJson │ Output   │  26%  │ text (read-more 80)      │ —
 * durationMs │ Duration │  14%  │ custom (`1,204 ms`)      │ —
 * createdAt  │ Created  │  20%  │ date (locale)            │ —
 *
 * The old table showed exactly these five fields, so nothing was dropped. The hand-rolled
 * `truncate()` helper is gone — `maxLength` + `showReadMore` is the grid's own version of it, and
 * it can expand in place instead of ending at an ellipsis.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * NO SEARCH BOX AND NO FILTER POPOVER, DELIBERATELY: `agentFunctionService.getAgentFunctionRequests`
 * accepts exactly `pageNumber`, `pageSize` and `status`. There is no free-text search over request
 * payloads, and `status` is what the tabs already own. Enabling the toolbar's search field would
 * put a control on screen whose value the service drops on the floor, so both
 * `cordio-datatable-search` and `cordio-datatable-filter-trigger` are absent on this page by
 * design. For the same reason no column is `sortable`.
 */

import { useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  Button,
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type ChipColor,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { EmptyState } from '@coordio/ui/layout';
import { ArrowLeft } from 'lucide-react';

import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { agentFunctionService } from '../services/agentFunctionService';
import { AgentFunctionRequestStatus } from '../types/agentFunction';
import { ROUTES } from '../utils/routes';

import type { AgentFunctionRequestDto } from '../types/agentFunction';

import './AgentFunctionRequestsListPage.scss';

/**
 * Quick-filter tabs over the request lifecycle, carrying exactly the options the old inline Status
 * dropdown offered. The All tab declares the literal `'all'` because the grid builds each tab's
 * test id as `cordio-datatable-status-tab-<value>`; an empty value emits
 * `cordio-datatable-status-tab-` and GRID-13's `…-status-tab-all` selector matches nothing.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: String(AgentFunctionRequestStatus.Pending) },
  { label: 'Processing', value: String(AgentFunctionRequestStatus.Processing) },
  { label: 'Completed', value: String(AgentFunctionRequestStatus.Completed) },
  { label: 'Failed', value: String(AgentFunctionRequestStatus.Failed) },
] as const;

/**
 * Synthetic toolbar key the status tabs write to.
 *
 * It cannot be `status` — that is the real API parameter, and the tabs also carry the value `'all'`,
 * which the endpoint has no meaning for. {@link toRequestQuery} translates the tab into a numeric
 * `status` and drops it entirely for All.
 */
const STATUS_TAB_PARAM_KEY = 'statusTab';

/** Turns the grid's query into the one `getAgentFunctionRequests` accepts. */
function toRequestQuery(query: Record<string, unknown>): Record<string, unknown> {
  const { [STATUS_TAB_PARAM_KEY]: statusTab, ...rest } = query;

  if (typeof statusTab === 'string' && statusTab !== 'all' && statusTab !== '') {
    const parsed = Number.parseInt(statusTab, 10);
    if (!Number.isNaN(parsed)) {
      return { ...rest, status: parsed };
    }
  }

  return rest;
}

/** The status vocabulary the API's enum values stand for. */
const STATUS_LABELS: Record<number, string> = {
  [AgentFunctionRequestStatus.Pending]: 'Pending',
  [AgentFunctionRequestStatus.Processing]: 'Processing',
  [AgentFunctionRequestStatus.Completed]: 'Completed',
  [AgentFunctionRequestStatus.Failed]: 'Failed',
};

const STATUS_COLORS: Record<number, ChipColor> = {
  [AgentFunctionRequestStatus.Pending]: 'default',
  [AgentFunctionRequestStatus.Processing]: 'amber',
  [AgentFunctionRequestStatus.Completed]: 'green',
  [AgentFunctionRequestStatus.Failed]: 'red',
};

export const AgentFunctionRequestsListPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // NO `listVersion` HERE: the reference keeps one refetch counter because it mutates rows, and
  // bumping it rebuilds `dataSource`. This page is read-only — nothing it can do invalidates the
  // list — so the counter would be state that never changes. The grid's own Refresh control covers
  // "show me what has happened since". Add one back the moment a mutation lands here.
  const functionDetailPath = id ? ROUTES.AGENT_FUNCTION_DETAIL.replace(':id', id) : ROUTES.AGENT_FUNCTIONS;

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<AgentFunctionRequestDto>((query) => {
        // Unreachable while the guard below renders instead of the grid; throwing rather than
        // silently resolving empty keeps a future regression visible.
        if (!id) throw new Error('No agent function in the route');
        return agentFunctionService.getAgentFunctionRequests(id, toRequestQuery(query));
      }),
    // `id` is the only invalidator on a read-only page — a route change means a different
    // function's history. No `listVersion` here: the grid's built-in Refresh covers "show me what
    // has happened since". Reinstate one the moment a mutation lands on this page.
    [id],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<AgentFunctionRequestDto>>>(
    () => [
      {
        field: 'status',
        header: 'Status',
        type: 'custom',
        width: '14%',
        customCell: (row) => (
          <Chip label={STATUS_LABELS[row.status] ?? 'Unknown'} color={STATUS_COLORS[row.status] ?? 'default'} />
        ),
      },
      // Read-more rather than a hard truncation: payloads are JSON, and the interesting part is
      // often just past the first 80 characters.
      { field: 'inputJson', header: 'Input', type: 'text', width: '26%', maxLength: 80, showReadMore: true },
      { field: 'outputJson', header: 'Output', type: 'text', width: '26%', maxLength: 80, showReadMore: true },
      {
        field: 'durationMs',
        header: 'Duration',
        type: 'custom',
        width: '14%',
        // The unit lives in the cell, not the header: a bare number in a history table reads as a
        // count. Separators because agent runs are routinely five digits of milliseconds.
        customCell: (row) => <>{row.durationMs != null ? `${row.durationMs.toLocaleString()} ms` : '—'}</>,
      },
      { field: 'createdAt', header: 'Created', type: 'date', dateFormat: 'locale', width: '20%' },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      // No `search` and no `filters`: see the file header — the endpoint honours neither.
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Execution status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
    }),
    [],
  );

  const openRequest = useCallback(
    (row: AgentFunctionRequestDto) =>
      navigate(ROUTES.AGENT_FUNCTION_REQUEST_DETAIL.replace(':requestId', row.id)),
    [navigate],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<AgentFunctionRequestDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View execution',
        onClick: openRequest,
      },
    ],
    [openRequest],
  );

  /**
   * Breadcrumbs rendered here rather than with `BreadcrumbTrail`: that component emits plain
   * `<a href>` elements, which would full-reload the SPA. React Router's `Link` keeps the client
   * navigation the old MUI breadcrumbs had.
   */
  const breadcrumbs = (
    <nav aria-label="Breadcrumb" className="agent-function-requests__breadcrumbs">
      <Link className="agent-function-requests__crumb" to={ROUTES.AGENT_FUNCTIONS}>
        Agent Functions
      </Link>
      <span aria-hidden className="agent-function-requests__crumb-separator">
        /
      </span>
      {id ? (
        <Link className="agent-function-requests__crumb" to={functionDetailPath}>
          Function
        </Link>
      ) : (
        <span className="agent-function-requests__crumb">Function</span>
      )}
      <span aria-hidden className="agent-function-requests__crumb-separator">
        /
      </span>
      <span aria-current="page" className="agent-function-requests__crumb agent-function-requests__crumb--current">
        Execution History
      </span>
    </nav>
  );

  if (!id) {
    return (
      <div className="agent-function-requests" data-testid="agent-function-request-list-page">
        <ShellHeader
          title="Execution History"
          description="Every call made to one agent function, with its input, output and duration."
          data-testid="agent-function-request-list-header"
          breadcrumbs={breadcrumbs}
        />
        <EmptyState
          title="No agent function selected"
          description="Open a function from the Agent Functions list to see its execution history."
          data-testid="agent-function-request-empty-state"
          action={
            <Button type="button" variant="outline" size="sm" onClick={() => navigate(ROUTES.AGENT_FUNCTIONS)}>
              Back to Agent Functions
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="agent-function-requests" data-testid="agent-function-request-list-page">
      <ShellHeader
        title="Execution History"
        description="Every call made to this agent function, with its input, output and duration."
        data-testid="agent-function-request-list-header"
        breadcrumbs={breadcrumbs}
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => navigate(functionDetailPath)}
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
            Back to function
          </Button>
        }
      />

      <div data-testid="agent-function-request-list-grid">
        <CordioAbpPagedDataTable<AgentFunctionRequestDto>
          className="agent-function-requests__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={openRequest}
        />
      </div>
    </div>
  );
};
